import {
  useState, useContext, useEffect, useCallback,
} from 'preact/hooks';
import { lazy, Suspense } from 'preact/compat';
import { Router } from 'preact-router';
import { IntlProvider } from 'preact-i18n';

import Home from '@pages/home';
import { Context } from '@store';
import setTheme from '@services/setTheme';

import Snackbar from './Snackbar';
import Header from './Header';
import BottomNavbar from './BottomNavbar';

const Control = lazy(() => import('@routes/control'));
const More = lazy(() => import('@routes/more'));
const Settings = lazy(() => import('@routes/settings'));
const SettingsLanguage = lazy(() => import('@routes/settings/language'));
const SettingsTheme = lazy(() => import('@routes/settings/theme'));
const Patreon = lazy(() => import('@routes/patreon'));

function App() {
  const [definition, setDefinition] = useState({});
  const [isControlPage, setIsControlPage] = useState(false);

  const [{
    theme, language, vibrate, wakeLock, swapButtons, isTWA,
  }, dispatch] = useContext(Context);

  useEffect(() => {
    checkServiceWorker();

    if (typeof window !== 'undefined') {
      if (isTWA) {
        window.gtag('event', 'playstore', {
          event_category: 'about',
          event_label: document.referrer,
        });
      }
      window.gtag('event', 'version', {
        event_category: 'about',
        event_label: process.env.APP_VERSION,
      });
      window.gtag('event', 'referrer', {
        event_category: 'about',
        event_label: document.referrer || 'false',
      });
    }
  }, []);

  useEffect(() => {
    changeLanguage(language);
    window.gtag('event', 'language', {
      event_category: 'settings',
      event_label: language,
    });
  }, [language]);

  useEffect(() => {
    window.gtag('event', 'theme', {
      event_category: 'settings',
      event_label: theme,
    });

    setTheme(theme);
    if (theme === 'auto') {
      const prefersColorScheme = window.matchMedia('(prefers-color-scheme: dark)');
      prefersColorScheme?.addEventListener('change', themeListener);
      return () => prefersColorScheme?.removeEventListener('change', themeListener);
    }
  }, [theme]);

  const themeListener = useCallback((e) => {
    const payload = e?.matches ? 'dark' : 'light';
    setTheme(payload);
  }, [theme]);

  useEffect(() => {
    window.gtag('event', 'vibrate', {
      event_category: 'settings',
      event_label: vibrate,
    });
  }, [vibrate]);

  useEffect(() => {
    window.gtag('event', 'wakeLock', {
      event_category: 'settings',
      event_label: wakeLock,
    });
  }, [wakeLock]);

  useEffect(() => {
    window.gtag('event', 'swapButtons', {
      event_category: 'settings',
      event_label: swapButtons,
    });
  }, [swapButtons]);

  const checkServiceWorker = () => {
    if ('serviceWorker' in navigator && process.env.NODE_ENV !== 'development') {
      navigator.serviceWorker.register('/sw.js').then((reg) => {
        // eslint-disable-next-line no-param-reassign
        reg.onupdatefound = () => {
          const e = reg.installing;
          e.onstatechange = () => {
            if (e.state !== 'installed') return;
            if (!navigator.serviceWorker.controller) return;
            window.swUpdate = true;
            dispatch({ type: 'SET_IS_SNACKBAR_SHOW', payload: true });
          };
        };
      }).catch(() => {});
    }
  };

  const handleRoute = (e) => {
    if (e.previous) dispatch({ type: 'SET_IS_SNACKBAR_SHOW', payload: false });
    setIsControlPage(e.active[0].props.path === '/:id');
    if (typeof window !== 'undefined') {
      if (window.swUpdate) {
        window.location = e.url;
        return;
      }
      window.gtag('config', process.env.GTAG_PROPERTY_ID, {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
      });
    }
  };

  const changeLanguage = async (lang) => {
    try {
      const e = await import(/* webpackChunkName: "i18n" */ `@i18n/${lang}.json`);
      setDefinition(e.default);
    } catch (e) {
      setDefinition({});
    }
  };

  return (
    <IntlProvider definition={definition}>
      <div id="app">
        <Header />
        <Suspense fallback={null}>
          <Router onChange={handleRoute}>
            <Home path="/" />
            <Settings path="/settings" />
            <SettingsLanguage path="/settings/language" />
            <SettingsTheme path="/settings/theme" />
            <More path="/more" />
            <Patreon path="/patreon" />
            <Control path="/:id" />
          </Router>
        </Suspense>
        <BottomNavbar />
        <Snackbar tall={isControlPage} />
      </div>
    </IntlProvider>
  );
}

export default App;
